import {AnalyticsService} from 'src/analytics';
import type {BookingSlot} from 'src/types/scheduler/settings';
import type {Template} from 'src/types/scheduler/templates';
import type {SchedulerTemplateForm} from 'src/types/scheduler/template-form';
import type {RefObject} from 'react';
import type {ScheduledEvent} from 'src/types/scheduler/events';
import type {SchedulerTemplate} from 'src/types/scheduler';

import {
  SCHEDULER_AMPLITUDE_EVENTS,
  SCHEDULER_TEAM_PERFORMANCE_ANALYTICS_PAGE_NAME,
  SCHEDULER_SINGLE_EVENT_DETAIL_INITIATED_FROM,
  type SchedulerMeetingsDrilldownRouteType,
} from './scheduler-amplitude-constants';
import {
  buildTemplateCreatedAmplitudeProps,
  buildSettingsWorkingHoursSavedAmplitudeProps,
  getSchedulerAmplitudeTemplateTypeLabel,
  getTemplateUpdatedFieldsChanged,
  getMeetingsDrilldownExportTableContext,
  getSchedulerDashboardUserRoleLabel,
  templatesPageLabel,
  buildDirectMeetingScheduledAmplitudeProps,
  buildEventDetailViewedAmplitudeProps,
  buildEventsEmptyStateViewedAmplitudeProps,
  buildEventsPageVisitedAmplitudeProps,
  getEventsEmptyStateAnalyticsDedupeKey,
  type EventsPageVisitedTimeFilter,
  type EventsPageVisitedViewType,
  type ScheduleNowClickedTimeFilter,
} from './scheduler-amplitude-helper';
import {getTemplateFormAnalyticsState} from './template-form-analytics-state';


export function trackDashboardPageVisited({
  agency_id,
  agency_name,
  isAdmin,
}: {
  agency_id: string;
  agency_name: string;
  isAdmin: boolean;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.DASHBOARD.DASHBOARD_PAGE_VISITED,
    {
      agency_id,
      agency_name,
      page_name: SCHEDULER_TEAM_PERFORMANCE_ANALYTICS_PAGE_NAME,
      user_role: getSchedulerDashboardUserRoleLabel(isAdmin),
    },
  );
}

export function trackEventsPageVisited({
  agency_id,
  agency_name,
  viewType,
  timeFilter,
  distributionRulesCsv,
}: {
  agency_id: string;
  agency_name: string;
  viewType: EventsPageVisitedViewType;
  timeFilter: EventsPageVisitedTimeFilter;
  distributionRulesCsv: string;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.EVENTS.EVENTS_PAGE_VISITED,
    {
      agency_id,
      agency_name,
      ...buildEventsPageVisitedAmplitudeProps({
        viewType,
        timeFilter,
        distributionRulesCsv,
      }),
    },
  );
}

export function trackEventsEmptyStateViewed({
  agency_id,
  agency_name,
  timeFilter,
  distributionRulesCsv,
}: {
  agency_id: string;
  agency_name: string;
  timeFilter: EventsPageVisitedTimeFilter;
  distributionRulesCsv: string;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.EVENTS.EVENTS_EMPTY_STATE_VIEWED,
    {
      agency_id,
      agency_name,
      ...buildEventsEmptyStateViewedAmplitudeProps({
        timeFilter,
        distributionRulesCsv,
      }),
    },
  );
}

export function trackDashboardStatCardClicked({
  agency_id,
  agency_name,
  cardType,
  cardValue,
  cardDuration,
}: {
  agency_id: string;
  agency_name: string;
  cardType: string;
  cardValue: number;
  cardDuration: number;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.DASHBOARD.STAT_CARD_CLICKED,
    {
      agency_id,
      agency_name,
      card_type: cardType,
      card_value: cardValue,
      card_duration: cardDuration,
    },
  );
}

export function trackEventsEmptyStateViewedIfDedupeKeyChanged(
  lastTrackedEmptyStateKeyRef: RefObject<string | null>,
  {
    agency_id,
    agency_name,
    timeFilter,
    distributionRulesCsv,
  }: {
    agency_id: string;
    agency_name: string;
    timeFilter: EventsPageVisitedTimeFilter;
    distributionRulesCsv: string;
  },
): void {
  const trackKey = getEventsEmptyStateAnalyticsDedupeKey(
    timeFilter,
    distributionRulesCsv,
  );
  if (lastTrackedEmptyStateKeyRef.current === trackKey) {
    return;
  }
  lastTrackedEmptyStateKeyRef.current = trackKey;
  trackEventsEmptyStateViewed({
    agency_id,
    agency_name,
    timeFilter,
    distributionRulesCsv,
  });
}

export function trackEventDetailViewed({
  agency_id,
  agency_name,
  event,
  eventsTimeFilter,
}: {
  agency_id: string;
  agency_name: string;
  event: ScheduledEvent;
  eventsTimeFilter: EventsPageVisitedTimeFilter;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.EVENTS.EVENT_DETAIL_VIEWED,
    {
      agency_id,
      agency_name,
      ...buildEventDetailViewedAmplitudeProps(event, eventsTimeFilter),
    },
  );
}

export function trackMeetingsDrilldownExportClicked({
  agency_id,
  agency_name,
  drilldownType,
  isFromTeamMemberDrilldown,
  rowCount,
}: {
  agency_id: string;
  agency_name: string;
  drilldownType: SchedulerMeetingsDrilldownRouteType;
  isFromTeamMemberDrilldown: boolean;
  rowCount: number;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.DASHBOARD.MEETINGS_TABLE_EXPORT_CLICKED,
    {
      agency_id,
      agency_name,
      table_context: getMeetingsDrilldownExportTableContext(
        drilldownType,
        isFromTeamMemberDrilldown,
      ),
      row_count: rowCount,
    },
  );
}

export function trackTeamMemberRowClicked({
  agency_id,
  agency_name,
  memberName,
  memberId,
}: {
  agency_id: string;
  agency_name: string;
  memberName: string;
  memberId: string;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.DASHBOARD.TEAM_MEMBER_ROW_CLICKED,
    {
      agency_id,
      agency_name,
      member_name: memberName,
      member_id: memberId,
    },
  );
}

export function trackMemberProfileViewed({
  agency_id,
  agency_name,
  memberName,
  memberId,
  meetingsThisWeek,
  totalHoursWeek,
  availableHours,
}: {
  agency_id: string;
  agency_name: string;
  memberName: string;
  memberId: string;
  meetingsThisWeek: number;
  totalHoursWeek: number;
  availableHours: number;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.DASHBOARD.MEMBER_PROFILE_VIEWED,
    {
      agency_id,
      agency_name,
      member_name: memberName,
      member_id: memberId,
      meetings_this_week: meetingsThisWeek,
      total_hours_week: totalHoursWeek,
      available_hours: availableHours,
    },
  );
}

export function trackMeetingAssignmentConfirmed({
  agency_id,
  agency_name,
  meetingsAssigned,
  reason,
  originalMember,
  newHost,
}: {
  agency_id: string;
  agency_name: string;
  meetingsAssigned: number;
  reason: string;
  originalMember: string;
  newHost: string;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.DASHBOARD.MEETING_ASSIGNMENT_CONFIRMED,
    {
      agency_id,
      agency_name,
      meetings_assigned: meetingsAssigned,
      reason,
      original_member: originalMember,
      new_host: newHost,
    },
  );
}

export function trackMeetingRescheduleRequested({
  agency_id,
  agency_name,
  meetingsCount,
  reason,
  memberName,
}: {
  agency_id: string;
  agency_name: string;
  meetingsCount: number;
  reason: string;
  memberName: string;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.DASHBOARD.MEETING_RESCHEDULE_REQUESTED,
    {
      agency_id,
      agency_name,
      meetings_count: meetingsCount,
      reason,
      member_name: memberName,
    },
  );
}

export function trackMeetingCancellationConfirmed({
  agency_id,
  agency_name,
  meetingsCount,
  reason,
  memberName,
}: {
  agency_id: string;
  agency_name: string;
  meetingsCount: number;
  reason: string;
  memberName: string;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.DASHBOARD.MEETING_CANCELLATION_CONFIRMED,
    {
      agency_id,
      agency_name,
      meetings_count: meetingsCount,
      reason,
      member_name: memberName,
    },
  );
}

export function trackAssignMeetingsPanelOpened({
  agency_id,
  agency_name,
  memberName,
  memberId,
}: {
  agency_id: string;
  agency_name: string;
  memberName: string;
  memberId: string;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.DASHBOARD.ASSIGN_MEETINGS_PANEL_OPENED,
    {
      agency_id,
      agency_name,
      member_name: memberName,
      member_id: memberId,
    },
  );
}

export function trackRescheduleCancelOptionSelected({
  agency_id,
  agency_name,
  memberName,
  memberId,
}: {
  agency_id: string;
  agency_name: string;
  memberName: string;
  memberId: string;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.DASHBOARD.RESCHEDULE_CANCEL_OPTION_SELECTED,
    {
      agency_id,
      agency_name,
      member_name: memberName,
      member_id: memberId,
    },
  );
}

export function trackUpdateScheduleDropdownOpened({
  agency_id,
  agency_name,
  memberName,
  memberId,
}: {
  agency_id: string;
  agency_name: string;
  memberName: string;
  memberId: string;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.DASHBOARD.UPDATE_SCHEDULE_DROPDOWN_OPENED,
    {
      agency_id,
      agency_name,
      member_name: memberName,
      member_id: memberId,
    },
  );
}

export function trackAssignMeetingHostSelected({
  agency_id,
  agency_name,
  meetingTitle,
  meetingId,
  selectedHost,
  hostRank,
}: {
  agency_id: string;
  agency_name: string;
  meetingTitle: string;
  meetingId: string;
  selectedHost: string;
  hostRank: number;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.DASHBOARD.ASSIGN_MEETING_HOST_SELECTED,
    {
      agency_id,
      agency_name,
      meeting_title: meetingTitle,
      meeting_id: meetingId,
      selected_host: selectedHost,
      host_rank: hostRank,
    },
  );
}

export function trackIndividualTemplatesPageVisited(
  agency_id: string,
  agency_name: string,
): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.TEMPLATE.TEMPLATES_PAGE_VISITED,
    {agency_id, agency_name},
  );
}

export function trackTeamTemplatesPageVisited(
  agency_id: string,
  agency_name: string,
): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.TEMPLATE.TEAM_TEMPLATES_PAGE_VISITED,
    {agency_id, agency_name},
  );
}

export function trackTemplateCreationCancelled(
  agency_id: string,
  agency_name: string,
  isTeamTemplate: boolean,
): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.TEMPLATE.TEMPLATE_CREATION_CANCELLED,
    {
      agency_id,
      agency_name,
      page: templatesPageLabel(isTeamTemplate),
    },
  );
}

export function trackScheduleNowClicked({
  agency_id,
  agency_name,
  activeTimeFilter,
}: {
  agency_id: string;
  agency_name: string;
  activeTimeFilter: ScheduleNowClickedTimeFilter;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.DIRECT_SCHEDULING.SCHEDULE_NOW_CLICKED,
    {
      agency_id,
      agency_name,
      active_time_filter: activeTimeFilter,
    },
  );
}

export function trackCopyLinkClicked({
  agency_id,
  agency_name,
  isTeamTemplate,
  urlType,
  distributionRule,
}: {
  agency_id: string;
  agency_name: string;
  isTeamTemplate: boolean;
  urlType: string;
  distributionRule: string;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.TEMPLATE.COPY_LINK_CLICKED,
    {
      agency_id,
      agency_name,
      template_type: getSchedulerAmplitudeTemplateTypeLabel(distributionRule),
      page: templatesPageLabel(isTeamTemplate),
      url_type: urlType,
    },
  );
}

export function trackDirectMeetingScheduled({
  agency_id,
  agency_name,
  template,
  meetingDate,
  startTime,
  viewerTimezone,
}: {
  agency_id: string;
  agency_name: string;
  template: SchedulerTemplate;
  meetingDate: string;
  startTime: string;
  viewerTimezone: string;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.DIRECT_SCHEDULING.DIRECT_MEETING_SCHEDULED,
    {
      agency_id,
      agency_name,
      ...buildDirectMeetingScheduledAmplitudeProps(template, {
        meetingDate,
        startTime,
        viewerTimezone,
      }),
    },
  );
}

export function trackScheduleNowInterviewerAdded({
  agency_id,
  agency_name,
  interviewerRole,
  interviewersCountAfter,
}: {
  agency_id: string;
  agency_name: string;
  interviewerRole: string;
  interviewersCountAfter: number;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.DIRECT_SCHEDULING.SCHEDULE_NOW_INTERVIEWER_ADDED,
    {
      agency_id,
      agency_name,
      interviewer_role: interviewerRole,
      interviewers_count_after: interviewersCountAfter,
    },
  );
}

export function trackScheduleNowTimeSlotSelected({
  agency_id,
  agency_name,
  date_selected,
  time_slot_selected,
  duration_minutes,
  week_navigation_count,
  has_interviewers,
}: {
  agency_id: string;
  agency_name: string;
  date_selected: string;
  time_slot_selected: string;
  duration_minutes: number;
  week_navigation_count: number;
  has_interviewers: boolean;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.DIRECT_SCHEDULING
      .SCHEDULE_NOW_TIME_SLOT_SELECTED,
    {
      agency_id,
      agency_name,
      date_selected,
      time_slot_selected,
      duration_minutes,
      week_navigation_count,
      has_interviewers,
    },
  );
}

export function trackSchedulerEventCancelled({
  agency_id,
  agency_name,
  cancellationReason,
  meetingType,
  eventDate,
  eventsCount,
}: {
  agency_id: string;
  agency_name: string;
  cancellationReason: string;
  meetingType: string;
  eventDate: string;
  eventsCount: number;
}): void {
  AnalyticsService.track(SCHEDULER_AMPLITUDE_EVENTS.EVENTS.EVENT_CANCELLED, {
    agency_id,
    agency_name,
    cancellation_reason: cancellationReason,
    meeting_type: meetingType,
    event_date: eventDate,
    events_count: eventsCount,
  });
}

export function trackSchedulerRescheduleRequested({
  agency_id,
  agency_name,
  rescheduleReason,
  meetingType,
  eventDate,
  reschedulesRemainingBefore,
  eventsCount,
}: {
  agency_id: string;
  agency_name: string;
  rescheduleReason: string;
  meetingType: string;
  eventDate: string;
  reschedulesRemainingBefore: number;
  eventsCount: number;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.EVENTS.RESCHEDULE_REQUESTED,
    {
      agency_id,
      agency_name,
      reschedule_reason: rescheduleReason,
      meeting_type: meetingType,
      event_date: eventDate,
      reschedules_remaining_before: reschedulesRemainingBefore,
      events_count: eventsCount,
    },
  );
}

export function trackTemplateCreated({
  agency_id,
  agency_name,
  template,
  isTeamTemplate,
}: {
  agency_id: string;
  agency_name: string;
  template: Template;
  isTeamTemplate: boolean;
}): void {
  AnalyticsService.track(SCHEDULER_AMPLITUDE_EVENTS.TEMPLATE.TEMPLATE_CREATED, {
    agency_id,
    agency_name,
    page: templatesPageLabel(isTeamTemplate),
    ...buildTemplateCreatedAmplitudeProps(template as SchedulerTemplateForm),
  });
}

export function trackTemplateDeleted({
  agency_id,
  agency_name,
  distributionRule,
  pageIsTeamTemplate,
}: {
  agency_id: string;
  agency_name: string;
  distributionRule: string;
  pageIsTeamTemplate: boolean;
}): void {
  AnalyticsService.track(SCHEDULER_AMPLITUDE_EVENTS.TEMPLATE.TEMPLATE_DELETED, {
    agency_id,
    agency_name,
    template_type: getSchedulerAmplitudeTemplateTypeLabel(distributionRule),
    page: templatesPageLabel(pageIsTeamTemplate),
  });
}

export function trackTemplateSaveSuccess({
  templateFormBeforeOverwrite,
  agency,
}: {
  templateFormBeforeOverwrite: SchedulerTemplateForm;
  agency: {id: string; name: string};
}): void {
  const baseline = getTemplateFormAnalyticsState();
  if (baseline == null) {
    return;
  }
  const fieldsChanged = getTemplateUpdatedFieldsChanged(
    baseline,
    templateFormBeforeOverwrite,
  );
  if (fieldsChanged === '') {
    return;
  }

  AnalyticsService.track(SCHEDULER_AMPLITUDE_EVENTS.TEMPLATE.TEMPLATE_UPDATED, {
    agency_id: agency.id,
    agency_name: agency.name,
    template_type: getSchedulerAmplitudeTemplateTypeLabel(
      templateFormBeforeOverwrite.distributionRule,
    ),
    fields_changed: fieldsChanged,
  });
}

export function trackSchedulerCalendarConnected({
  agency_id,
  agency_name,
  calendarType,
}: {
  agency_id: string;
  agency_name: string;
  calendarType: string;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.SETTINGS.CALENDAR_CONNECTED,
    {
      agency_id,
      agency_name,
      calendar_type: calendarType,
    },
  );
}

export function trackCancelEventInitiated({
  agency_id,
  agency_name,
  meetingType,
  eventDate,
  initiatedFrom,
}: {
  agency_id: string;
  agency_name: string;
  meetingType: string;
  eventDate: string;
  initiatedFrom: typeof SCHEDULER_SINGLE_EVENT_DETAIL_INITIATED_FROM;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.EVENTS.CANCEL_EVENT_INITIATED,
    {
      agency_id,
      agency_name,
      meeting_type: meetingType,
      event_date: eventDate,
      initiated_from: initiatedFrom,
    },
  );
}

export function trackSettingsDisconnectAccountClicked({
  agency_id,
  agency_name,
  calendarType,
}: {
  agency_id: string;
  agency_name: string;
  calendarType: string;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.SETTINGS.DISCONNECT_ACCOUNT_CLICKED,
    {
      agency_id,
      agency_name,
      calendar_type: calendarType,
    },
  );
}

export function trackRequestRescheduleInitiated({
  agency_id,
  agency_name,
  meetingType,
  eventDate,
  reschedulesRemaining,
  initiatedFrom,
}: {
  agency_id: string;
  agency_name: string;
  meetingType: string;
  eventDate: string;
  reschedulesRemaining: number;
  initiatedFrom: typeof SCHEDULER_SINGLE_EVENT_DETAIL_INITIATED_FROM;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.EVENTS.REQUEST_RESCHEDULE_INITIATED,
    {
      agency_id,
      agency_name,
      meeting_type: meetingType,
      event_date: eventDate,
      reschedules_remaining: reschedulesRemaining,
      initiated_from: initiatedFrom,
    },
  );
}

export function trackSettingsConnectToDifferentAccountClicked({
  agency_id,
  agency_name,
  currentCalendar,
}: {
  agency_id: string;
  agency_name: string;
  currentCalendar: string;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.SETTINGS.CONNECT_TO_DIFFERENT_ACCOUNT_CLICKED,
    {
      agency_id,
      agency_name,
      current_calendar: currentCalendar,
    },
  );
}

export function trackSettingsMeetingCadenceUpdated({
  agency_id,
  agency_name,
  previousValue,
  newValue,
}: {
  agency_id: string;
  agency_name: string;
  previousValue: number;
  newValue: number;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.SETTINGS.MEETING_CADENCE_UPDATED,
    {
      agency_id,
      agency_name,
      previous_value: previousValue,
      new_value: newValue,
    },
  );
}

export function trackSettingsWorkingHoursSaved({
  agency_id,
  agency_name,
  timezone,
  bookingSlots,
}: {
  agency_id: string;
  agency_name: string;
  timezone: string;
  bookingSlots: BookingSlot[];
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.SETTINGS.WORKING_HOURS_SAVED,
    {
      agency_id,
      agency_name,
      ...buildSettingsWorkingHoursSavedAmplitudeProps(timezone, bookingSlots),
    },
  );
}

export function trackSettingsAccountDisconnectConfirmed({
  agency_id,
  agency_name,
  calendarType,
}: {
  agency_id: string;
  agency_name: string;
  calendarType: string;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.SETTINGS.ACCOUNT_DISCONNECT_CONFIRMED,
    {
      agency_id,
      agency_name,
      calendar_type: calendarType,
    },
  );
}
